import React, { useState, useEffect, useCallback } from 'react';
import { satisfaction as api } from '../services/api';
import { NotePastille, etiquetteFormulaire, estPassage, VehiculeEnvoi, formatPlaque } from './SatisfactionCommun';
import { BlocProjet, EtatProjet } from './SatisfactionProjet';

/**
 * Fiche d'une réponse client (refonte du 2026-10-16).
 *
 * Deux colonnes qui défilent chacune de leur côté, pied de fiche fixe :
 *  - à gauche, ce que le client a dit (commentaires d'abord, puis la note sur
 *    son échelle 0-10, les étoiles, les OUI/NON), le parcours daté et les
 *    coordonnées ;
 *  - à droite, et plus large, le traitement : canal, phrase d'intention,
 *    brouillon rédigé par l'IA et modifiable. C'est là que l'on écrit : la
 *    rédaction a besoin de largeur, la lecture des réponses beaucoup moins.
 * Un client qui n'a pas répondu n'a rien à traiter : pas de bloc de réponse.
 */

const CANAUX = [
  { cle: 'telephone', label: '📞 Appel', emet: false },
  { cle: 'sms', label: '📱 SMS', emet: true },
  { cle: 'mail', label: '✉️ E-mail', emet: true },
  { cle: 'sur_place', label: '🏢 Sur place', emet: false },
];

const CANAL_LABEL = { telephone: 'téléphone', sms: 'SMS', mail: 'e-mail', sur_place: 'sur place', note: 'note' };
const CANAL_ENVOI = { email: 'e-mail', sms: 'SMS', 'sms+email': 'SMS + e-mail' };
const FINALITE_LABEL = { prospection_tel: '📞 Téléphone', prospection_sms: '📱 SMS', prospection_email: '✉️ E-mail' };

function fmtDateHeure(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  const opts = { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };
  if (x.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return x.toLocaleString('fr-FR', opts).replace(' ', ' · ');
}

// Date sans heure (« 2026-09-29 ») : découpée telle quelle, sans `new Date`
// (lecture UTC qui pourrait reculer d'un jour).
function fmtJour(d) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

// Longueur comptée comme Mobbiz : texte désaccentué (cf. satisfaction-messages.js).
const longueurSms = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').length;

const aRepondu = (r) => (r.valeur_bool !== null && r.valeur_bool !== undefined)
  || (r.valeur_num !== null && r.valeur_num !== undefined)
  || Boolean(r.valeur_texte)
  || (r.type === 'recontact' && Object.keys(r.valeur_json?.choix || {}).length > 0);

// Bloc « Recontact » : les cases cochées, et leur sort chez A97 RGPD (qui
// détient la preuve).
function Recontact({ r, consentements = [] }) {
  const choix = r.valeur_json?.choix || {};
  const lignes = consentements.filter((c) => c.choix && Object.keys(c.choix).length);
  return (
    <div className="sat-recontact">
      <div>
        {Object.keys(choix).map((code) => (
          <span key={code} className={choix[code] ? 'status-success' : 'sat-vide-inline'}>
            {FINALITE_LABEL[code] || code} {choix[code] ? '✓' : '—'}
          </span>
        ))}
      </div>
      {lignes.map((c) => (
        <div key={c.id} className="sat-meta">
          {Object.keys(c.choix).map((k) => FINALITE_LABEL[k] || k).join(' + ')} :{' '}
          {c.statut === 'enregistre' && (c.preuve_url
            ? <a href={c.preuve_url} target="_blank" rel="noopener noreferrer">✓ preuve A97 RGPD</a>
            : <span className="status-success">✓ enregistré dans A97 RGPD</span>)}
          {c.statut === 'a_envoyer' && (
            <span className="status-warning" title={c.erreur || ''}>
              ⏳ en attente de transmission à A97 RGPD ({c.tentatives} tentative{c.tentatives > 1 ? 's' : ''})
            </span>
          )}
          {c.statut === 'echec' && <span className="status-error">✕ refusé par A97 RGPD : {c.erreur}</span>}
        </div>
      ))}
    </div>
  );
}

// La note NPS sur son échelle : on la lit comme le client l'a donnée.
function EchelleNps({ n }) {
  const ton = n >= 9 ? 'sat-np-pro' : 'sat-np-det';
  return (
    <div className="sat-f-echelle" aria-label={`Note ${n} sur 10`}>
      {Array.from({ length: 11 }, (_, i) => (
        <span key={i} className={i === n ? `sat-f-echelle-sel ${ton}` : ''}>{i}</span>
      ))}
    </div>
  );
}

function Valeur({ r, consentements }) {
  if (r.type === 'recontact') return <Recontact r={r} consentements={consentements} />;
  if (r.valeur_bool !== null && r.valeur_bool !== undefined) {
    return <span className={`sat-f-oui-non ${r.valeur_bool ? 'sat-f-oui' : 'sat-f-non'}`}>{r.valeur_bool ? 'Oui' : 'Non'}</span>;
  }
  if (r.valeur_num !== null && r.valeur_num !== undefined) {
    const n = Number(r.valeur_num);
    if (r.type === 'nps') return <EchelleNps n={n} />;
    return (
      <span className="sat-f-etoiles" aria-label={`${n} sur 5`}>
        {'★'.repeat(n)}<span className="sat-f-etoiles-vides">{'★'.repeat(Math.max(0, 5 - n))}</span>
        <span className="sat-f-etoiles-n">{n}/5</span>
      </span>
    );
  }
  return <span>{r.valeur_texte}</span>;
}

export default function SatisfactionReponseModal({ envoiId, user, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [canal, setCanal] = useState('telephone');
  const [note, setNote] = useState('');
  const [objet, setObjet] = useState('');
  const [message, setMessage] = useState('');
  const [brouillonIa, setBrouillonIa] = useState(false);
  const [redaction, setRedaction] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [voirSansReponse, setVoirSansReponse] = useState(false);
  const [nouveauSuivi, setNouveauSuivi] = useState(false);
  const isSuperAdmin = user?.role === 'super-admin';

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.getEnvoi(envoiId);
      setData(d);
      if (!objet) setObjet(`${d.envoi.site_nom || d.envoi.concession_nom || 'Votre concession'} : suite à votre retour`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
    // `objet` volontairement hors dépendances : il ne doit être prérempli qu'une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envoiId]);

  useEffect(() => { charger(); }, [charger]);

  const canalCourant = CANAUX.find((c) => c.cle === canal);
  const emet = canalCourant?.emet;

  const proposer = async () => {
    setRedaction(true);
    setErreur('');
    try {
      const { data: d } = await api.brouillon(envoiId, { canal, note_operateur: note });
      if (!d.disponible) {
        setErreur('La rédaction assistée est indisponible pour le moment : écrivez le message vous-même.');
      } else {
        setMessage(d.brouillon);
        setBrouillonIa(true);
      }
    } catch (e) {
      setErreur(e.response?.data?.error || 'Rédaction impossible');
    } finally {
      setRedaction(false);
    }
  };

  const valider = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      await api.traiter(envoiId, {
        canal, note_operateur: note, objet: canal === 'mail' ? objet : null,
        message: emet ? message : null, brouillon_ia: brouillonIa,
      });
      setNote(''); setMessage(''); setBrouillonIa(false); setNouveauSuivi(false);
      await charger();
      if (onChanged) onChanged();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const annulerTraitement = async () => {
    try {
      await api.annulerTraitement(envoiId);
      await charger();
      if (onChanged) onChanged();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Annulation impossible');
    }
  };

  const e = data?.envoi;
  const reponses = data?.reponses || [];
  const nps = reponses.find((r) => r.type === 'nps' && r.valeur_num !== null && r.valeur_num !== undefined);
  const npsVal = nps ? Number(nps.valeur_num) : null;
  const textes = reponses.filter((r) => r.type === 'texte' && r.valeur_texte);
  const autres = reponses.filter((r) => r.type !== 'texte' && aRepondu(r));
  const sansReponse = reponses.filter((r) => !aRepondu(r));
  const repondu = e?.statut === 'repondu';
  const detracteur = npsVal !== null && npsVal <= 8;
  const composer = repondu && (!e.traite_le || nouveauSuivi);
  const projet = repondu ? data?.projet : null;
  const dernier = data?.traitements?.length ? data.traitements[data.traitements.length - 1] : null;

  const destinataire = canal === 'sms' ? e?.client_telephone : e?.client_email;
  const longueur = longueurSms(message);
  const peutValider = !envoi && !loading && composer
    && (emet ? Boolean(message.trim()) && Boolean(destinataire) : Boolean(note.trim()));
  // Avec un projet d'achat, deux suivis coexistent : le bouton dit lequel il clôt.
  const quoi = projet ? "l'avis " : '';
  const libelleAction = envoi ? 'Envoi…'
    : canal === 'sms' ? `Envoyer le SMS et marquer ${quoi}traité`
      : canal === 'mail' ? `Envoyer l'e-mail et marquer ${quoi}traité` : `Marquer ${quoi}traité`;

  const etat = !e ? null
    : e.statut === 'echec' ? <span className="status-error">Envoi jamais parti</span>
      : !repondu ? <span className="status-info">En attente de réponse</span>
        : e.traite_le ? <span className="status-success">✓ {projet ? 'Avis traité' : 'Traité'}</span>
          : detracteur ? <span className="status-error">Détracteur · à rappeler</span>
            : <span className="status-warning">À traiter</span>;

  const contexte = e ? [
    e.site_nom || e.site_code,
    e.marque,
    fmtJour(e.date_vente) && `${estPassage(e.formulaire_source) ? 'passage le' : 'vendu le'} ${fmtJour(e.date_vente)}`,
  ].filter(Boolean).join(' · ') : '';

  return (
    <div className="modal-overlay">
      <div className="modal-content sat-fiche" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-header sat-fiche-tete">
          {e ? <NotePastille nps={npsVal} statut={e.statut} grande /> : <span />}
          <div className="sat-fiche-titre">
            <h2 className="sat-fiche-nom">
              {e ? ([e.client_prenom, e.client_nom].filter(Boolean).join(' ') || 'Client') : 'Réponse client'}
              {e && <VehiculeEnvoi immat={e.immat} chassis={e.chassis} />}
              {etat}
              {projet && <EtatProjet statut={projet.projet_statut} joignable={projet.joignable} />}
            </h2>
            {e && (
              <div className="sat-in-sous sat-in-ligne-sous">
                <span className="sat-in-tag" title={e.formulaire_nom}>{etiquetteFormulaire(e.formulaire_source, e.formulaire_nom)}</span>
                {contexte}
              </div>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body sat-fiche-corps">
          {loading && !data && <div className="sat-vide">Chargement…</div>}

          {e && (
            <>
              <div className="sat-fiche-col sat-fiche-gauche">
                {textes.length > 0 && (
                  <section className="sat-f-bloc">
                    <h3 className="sat-f-titre">Ce que le client a écrit</h3>
                    {textes.map((r) => (
                      <figure key={r.question_id} className="sat-f-citation">
                        {textes.length > 1 && <figcaption>{r.libelle}</figcaption>}
                        <blockquote>{r.valeur_texte}</blockquote>
                      </figure>
                    ))}
                  </section>
                )}

                <section className="sat-f-bloc">
                  <h3 className="sat-f-titre">{repondu ? 'Ses réponses' : 'Questionnaire'}</h3>
                  {!repondu && <div className="sat-muet">Le client n'a pas encore répondu.</div>}
                  {autres.map((r) => (
                    <div key={r.question_id} className="sat-f-question">
                      <div className="sat-f-libelle">{r.libelle}</div>
                      <Valeur r={r} consentements={data.consentements} />
                    </div>
                  ))}
                  {repondu && sansReponse.length > 0 && (
                    <>
                      <button type="button" className="sat-f-replie" onClick={() => setVoirSansReponse((v) => !v)}>
                        {voirSansReponse ? '▾' : '▸'} {sansReponse.length} question{sansReponse.length > 1 ? 's' : ''} sans réponse
                      </button>
                      {voirSansReponse && sansReponse.map((r) => (
                        <div key={r.question_id} className="sat-f-libelle sat-f-sans">{r.libelle}</div>
                      ))}
                    </>
                  )}
                </section>

                <section className="sat-f-bloc">
                  <h3 className="sat-f-titre">Parcours</h3>
                  <ol className="sat-f-frise">
                    {fmtJour(e.date_vente) && (
                      <li className="sat-f-fait">
                        <span>{estPassage(e.formulaire_source) ? 'Passage atelier' : 'Vente'}</span>
                        <span className="sat-f-quand">{fmtJour(e.date_vente)}</span>
                      </li>
                    )}
                    <li className={e.statut === 'echec' ? 'sat-f-alerte' : 'sat-f-fait'}>
                      <span>
                        {e.statut === 'echec' ? 'Envoi échoué' : 'Questionnaire envoyé'} · {CANAL_ENVOI[e.canal_envoi] || e.canal_envoi}
                        {e.note_canal && <span className="sat-f-detail">{e.note_canal}</span>}
                        {e.statut === 'echec' && e.erreur && <span className="sat-f-detail">{e.erreur}</span>}
                      </span>
                      <span className="sat-f-quand">{fmtDateHeure(e.envoye_le)}</span>
                    </li>
                    {repondu ? (
                      <li className="sat-f-fait"><span>Réponse reçue{e.canal_reponse && ` par ${CANAL_ENVOI[e.canal_reponse] || e.canal_reponse}`}</span><span className="sat-f-quand">{fmtDateHeure(e.repondu_le)}</span></li>
                    ) : e.statut !== 'echec' && (
                      <li className="sat-f-attente"><span>En attente de réponse</span><span className="sat-f-quand" /></li>
                    )}
                    {data.traitements.map((t) => (
                      <li key={t.id} className={t.statut === 'echec' ? 'sat-f-alerte' : 'sat-f-fait'}>
                        <span>
                          Traité par {CANAL_LABEL[t.canal] || t.canal}
                          {(t.user_prenom || t.user_nom) && ` · ${[t.user_prenom, t.user_nom].filter(Boolean).join(' ')}`}
                          {t.brouillon_ia && <span className="sat-f-ia-mention"> · ✨ rédigé avec l'IA</span>}
                          {t.statut === 'echec' && <span className="sat-f-detail">Échec : {t.erreur}</span>}
                          {t.note_operateur && <span className="sat-f-detail">{t.note_operateur}</span>}
                          {t.message_envoye && (
                            <details className="sat-f-envoye">
                              <summary>Message envoyé</summary>
                              <div className="sat-message">
                                {t.objet && <b>{t.objet}<br /></b>}
                                {t.message_envoye}
                              </div>
                            </details>
                          )}
                        </span>
                        <span className="sat-f-quand">{fmtDateHeure(t.created_at)}</span>
                      </li>
                    ))}
                    {repondu && !e.traite_le && (
                      <li className="sat-f-maintenant">
                        <span>{detracteur ? 'À rappeler' : 'À traiter'}</span><span className="sat-f-quand">maintenant</span>
                      </li>
                    )}
                  </ol>
                </section>

                <section className="sat-f-bloc">
                  <h3 className="sat-f-titre">Coordonnées</h3>
                  <div className="sat-f-coord">
                    <div><span>Tél.</span> {e.client_telephone || <span className="sat-muet">aucun</span>}</div>
                    <div><span>E-mail</span> {e.client_email || <span className="sat-muet">aucun</span>}</div>
                    {(e.immat || e.chassis) && (
                      <>
                        <div><span>Plaque</span> {e.immat ? formatPlaque(e.immat) : <span className="sat-muet">pas encore immatriculé</span>}</div>
                        {e.chassis && <div><span>VIN</span> <span className="sat-f-vin">{e.chassis}</span></div>}
                      </>
                    )}
                  </div>
                </section>
              </div>

              <div className="sat-fiche-col sat-fiche-droite">
                {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

                {projet && (
                  <BlocProjet key={e.id} envoiId={e.id} user={user}
                    p={{ ...projet, client_telephone: e.client_telephone, client_email: e.client_email }}
                    onSaved={async () => { await charger(); if (onChanged) onChanged(); }} />
                )}
                {/* Encart « Avis du client » (2026-11-02), même gabarit que le
                    projet d'achat : un sujet = un encart, son état, ses boutons. */}
                <section className="sat-encart sat-av-encart">
                  <div className="sat-encart-tete">
                    <h3>💬 Avis du client</h3>
                    {etat}
                  </div>
                  {repondu && (
                    <p className="sat-encart-aide">
                      Sa satisfaction sur {e.formulaire_source === 'a97_sellens_ventes_vn' ? "l'achat de son véhicule" : 'son passage à l\'atelier'} :
                      on lui répond, on le rappelle s'il est mécontent.
                    </p>
                  )}

                {!repondu && (
                  <div className="sat-f-rien">
                    {e.statut === 'echec'
                      ? "Le questionnaire n'est jamais parti : ce client n'a rien reçu, il n'y a rien à traiter."
                      : "Le client n'a pas encore répondu. Il n'y a rien à traiter pour l'instant."}
                  </div>
                )}

                {repondu && e.traite_le && !nouveauSuivi && (
                  <div className="sat-f-traite">
                    <div>
                      <b>✓ Traité par {CANAL_LABEL[e.canal_traitement] || '—'}</b> le {fmtDateHeure(e.traite_le)}
                      {dernier && (dernier.user_prenom || dernier.user_nom) && ` · ${[dernier.user_prenom, dernier.user_nom].filter(Boolean).join(' ')}`}
                    </div>
                    {dernier?.note_operateur && <div className="sat-f-traite-detail">{dernier.note_operateur}</div>}
                    {dernier?.message_envoye && (
                      <div className="sat-message">
                        {dernier.objet && <b>{dernier.objet}<br /></b>}
                        {dernier.message_envoye}
                      </div>
                    )}
                    <div className="sat-f-traite-actions">
                      <button type="button" className="btn-secondary" onClick={() => setNouveauSuivi(true)}>+ Ajouter un suivi</button>
                      {isSuperAdmin && (
                        <button type="button" className="btn-secondary" onClick={annulerTraitement}>Annuler le marquage</button>
                      )}
                    </div>
                  </div>
                )}

                {composer && (
                  <section className="sat-f-traiter">
                    <div className="header-segmented sat-f-canaux">
                      {CANAUX.map((c) => (
                        <button key={c.cle} type="button"
                          className={`header-segmented-btn${canal === c.cle ? ' active' : ''}`}
                          onClick={() => { setCanal(c.cle); setBrouillonIa(false); }}>{c.label}</button>
                      ))}
                    </div>

                    {!emet && (
                      <label className="sat-f-etape">
                        <span className="sat-f-etape-lib">Ce qui a été dit au client</span>
                        <textarea className="sat-input" rows={5} value={note}
                          placeholder="ex. appelé ce matin, excuses pour l'attente, il était satisfait de l'explication"
                          onChange={(ev) => setNote(ev.target.value)} />
                      </label>
                    )}

                    {emet && (
                      <>
                        <div className="sat-f-etape">
                          <label className="sat-f-etape-lib" htmlFor="sat-intention">
                            <b className="sat-f-num">1</b> Ce que vous voulez lui dire, en une phrase
                          </label>
                          <div className="sat-f-intention">
                            <input id="sat-intention" className="sat-input" value={note}
                              placeholder="ex. excuses pour l'attente, le chef d'atelier le rappelle lundi"
                              onChange={(ev) => setNote(ev.target.value)}
                              onKeyDown={(ev) => { if (ev.key === 'Enter' && note.trim() && !redaction) proposer(); }} />
                            <button type="button" className="sat-f-btn-ia" onClick={proposer} disabled={redaction || !note.trim()}>
                              {redaction ? 'Rédaction…' : '✨ Rédiger'}
                            </button>
                          </div>
                        </div>

                        <div className="sat-f-etape sat-f-etape-message">
                          <span className="sat-f-etape-lib"><b className="sat-f-num">2</b> Relisez et corrigez</span>
                          <div className={`sat-f-brouillon${brouillonIa ? ' sat-f-brouillon-ia' : ''}`}>
                            {brouillonIa && (
                              <div className="sat-f-brouillon-tete">
                                <span>✨ Proposé par l'IA à partir de ses réponses</span>
                                <button type="button" className="sat-f-lien" onClick={proposer} disabled={redaction || !note.trim()}>
                                  ↻ Autre version
                                </button>
                              </div>
                            )}
                            {canal === 'mail' && (
                              <input className="sat-f-objet" value={objet} aria-label="Objet"
                                onChange={(ev) => setObjet(ev.target.value)} />
                            )}
                            <textarea className="sat-f-texte" value={message}
                              placeholder="Cliquez sur ✨ Rédiger, ou écrivez le message vous-même."
                              onChange={(ev) => setMessage(ev.target.value)} />
                            <div className="sat-f-brouillon-pied">
                              <span className={destinataire ? '' : 'sat-f-manque'}>
                                {destinataire ? `À ${destinataire}` : canal === 'sms' ? 'Aucun téléphone connu' : 'Aucun e-mail connu'}
                              </span>
                              {canal === 'sms' && (
                                <span className={longueur > 160 ? 'sat-f-manque' : ''}>{longueur} / 160</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </section>
                )}
                {composer && (
                  <div className="sat-encart-actions">
                    {nouveauSuivi && <button className="btn-secondary" onClick={() => setNouveauSuivi(false)}>Annuler</button>}
                    <button className="btn-primary" onClick={valider} disabled={!peutValider}>{libelleAction}</button>
                  </div>
                )}
                </section>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
